import React from "react";
import { Box, LinearProgress, Tooltip, Typography } from "@mui/material";
import { SyncOutlined } from "@mui/icons-material";
import { SessionSyncMap } from "../../types/chats";

interface BackgroundSyncIndicatorProps {
  syncingSessions: SessionSyncMap;
  isDarkTheme?: boolean;
}

/**
 * BackgroundSyncIndicator - Compact indicator shown in navbar
 *
 * Shows when chats are being pre-fetched in background after session connects.
 * Allows user to see progress without being on /chat page.
 */
export const BackgroundSyncIndicator: React.FC<
  BackgroundSyncIndicatorProps
> = ({ syncingSessions, isDarkTheme = false }) => {
  // Get all syncing sessions
  const sessions = Array.from(syncingSessions.values());

  if (sessions.length === 0) {
    return null; // Don't show anything if no sessions syncing
  }

  // Calculate overall progress
  const totalProcessed = sessions.reduce((sum, s) => sum + s.processed, 0);
  const totalCount = sessions.reduce((sum, s) => sum + s.total, 0);
  const overallProgress =
    totalCount > 0 ? (totalProcessed / totalCount) * 100 : 0;

  const backgroundColor = isDarkTheme
    ? "rgba(76, 54, 153, 0.1)"
    : "rgba(76, 54, 153, 0.08)";
  const progressColor = "#4C3699";
  const textColor = isDarkTheme
    ? "rgba(255, 255, 255, 0.7)"
    : "rgba(0, 0, 0, 0.6)";

  return (
    <Tooltip
      title={
        <Box>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
          >
            Loading chats in background
          </Typography>
          {sessions.map((session) => (
            <Typography
              key={session.sessionId}
              variant="caption"
              sx={{ display: "block", fontSize: "0.7rem" }}
            >
              {session.sessionName}: {session.processed}/{session.total}
            </Typography>
          ))}
        </Box>
      }
      placement="bottom"
      arrow
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.8,
          px: 1.5,
          py: 0.5,
          borderRadius: 2,
          backgroundColor,
          cursor: "pointer",
          transition: "background-color 0.2s",
          "&:hover": {
            backgroundColor: isDarkTheme
              ? "rgba(76, 54, 153, 0.15)"
              : "rgba(76, 54, 153, 0.12)",
          },
        }}
      >
        {/* Spinning icon */}
        <SyncOutlined
          sx={{
            fontSize: 16,
            color: progressColor,
            animation: "spin 2s linear infinite",
            "@keyframes spin": {
              "0%": { transform: "rotate(0deg)" },
              "100%": { transform: "rotate(360deg)" },
            },
          }}
        />

        {/* Progress text */}
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.75rem",
            fontWeight: 500,
            color: textColor,
            whiteSpace: "nowrap",
          }}
        >
          {Math.round(overallProgress)}%
        </Typography>

        {/* Compact progress bar */}
        <Box sx={{ width: 40, height: 3 }}>
          <LinearProgress
            variant="determinate"
            value={overallProgress}
            sx={{
              height: 3,
              borderRadius: 1.5,
              backgroundColor: "rgba(0, 0, 0, 0.08)",
              "& .MuiLinearProgress-bar": {
                backgroundColor: progressColor,
                borderRadius: 1.5,
              },
            }}
          />
        </Box>
      </Box>
    </Tooltip>
  );
};
